import axios from 'axios';

class RealtimeService {
  constructor() {
    this.socket = null;
    this.listeners = new Set();
    this.connectionListeners = new Set();
    this.reconnectTimer = null;
    this.reconnectAttempt = 0;
    this.authToken = localStorage.getItem('token');
    this.manuallyClosed = false;
    this.subscriptions = [];
  }

  setToken(token) {
    this.authToken = token || null;
    if (!this.authToken) {
      this.disconnect();
      return;
    }

    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.disconnect();
      this.connect();
    }
  }

  connect() {
    if (!this.authToken) return;
    if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) {
      return;
    }

    const rawBaseUrl = axios.defaults.baseURL || window.location.origin;
    const normalizedBaseUrl = rawBaseUrl.startsWith('http') ? rawBaseUrl : window.location.origin;
    const wsBaseUrl = normalizedBaseUrl.replace(/^http:/, 'ws:').replace(/^https:/, 'wss:');
    const endpoint = `${wsBaseUrl}/api/realtime?token=${encodeURIComponent(this.authToken)}`;

    this.manuallyClosed = false;
    this.socket = new WebSocket(endpoint);
    this.notifyConnectionState('connecting');

    this.socket.onopen = () => {
      this.reconnectAttempt = 0;
      this.notifyConnectionState('connected');
      this.sendSubscriptionMessage();
    };

    this.socket.onmessage = (event) => {
      try {
        const parsed = JSON.parse(event.data);
        if (parsed?.type === 'connection_ready') return;
        this.listeners.forEach((listener) => listener(parsed));
      } catch (error) {
        console.error('Realtime parse error:', error);
      }
    };

    this.socket.onerror = () => {
      this.notifyConnectionState('error');
    };

    this.socket.onclose = () => {
      this.notifyConnectionState('disconnected');
      this.socket = null;
      if (!this.manuallyClosed) {
        this.scheduleReconnect();
      }
    };
  }

  disconnect() {
    this.manuallyClosed = true;
    if (this.reconnectTimer) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
    this.notifyConnectionState('disconnected');
  }

  scheduleReconnect() {
    if (!this.authToken) return;
    const cappedAttempt = Math.min(this.reconnectAttempt, 6);
    const baseDelay = 1000 * (2 ** cappedAttempt);
    const jitter = Math.floor(Math.random() * 500);
    const delay = Math.min(baseDelay + jitter, 30000);
    this.reconnectAttempt += 1;

    this.reconnectTimer = window.setTimeout(() => {
      this.connect();
    }, delay);
  }

  subscribe(listener) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  setSubscriptions(entities = []) {
    this.subscriptions = Array.isArray(entities)
      ? entities.filter((entity) => typeof entity === 'string' && entity.trim() !== '')
      : [];
    this.sendSubscriptionMessage();
  }

  sendSubscriptionMessage() {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return;
    this.socket.send(JSON.stringify({
      type: 'subscribe',
      entities: this.subscriptions
    }));
  }

  onConnectionStateChange(listener) {
    this.connectionListeners.add(listener);
    return () => {
      this.connectionListeners.delete(listener);
    };
  }

  notifyConnectionState(state) {
    this.connectionListeners.forEach((listener) => listener(state));
  }
}

export const realtimeService = new RealtimeService();
